import { createBrowserRouter, Navigate } from "react-router-dom";
import { RequireAuth, RequireRole, RedirectIfAuthenticated } from "./guards.jsx";
import { LoginPage } from "@/features/auth/LoginPage.jsx";
import { LandingPage } from "@/features/institutional/LandingPage.jsx";
import { LegalPage } from "@/features/institutional/LegalPage.jsx";
import { DashboardPage } from "@/features/dashboard/DashboardPage.jsx";
import { LeadsPage } from "@/features/leads/LeadsPage.jsx";
import { CrmPage } from "@/features/crm/CrmPage.jsx";
import { AgendaPage } from "@/features/agenda/AgendaPage.jsx";
import { WhatsAppPage } from "@/features/whatsapp/WhatsAppPage.jsx";
import { AutomationPage } from "@/features/automation/AutomationPage.jsx";
import { IntegrationsPage } from "@/features/integrations/IntegrationsPage.jsx";
import { WhatsAppMetaPage } from "@/features/integrations/WhatsAppMetaPage.jsx";
import { SettingsPage } from "@/features/settings/SettingsPage.jsx";
import { UsersPage } from "@/features/users/UsersPage.jsx";
import { MasterDashboardPage } from "@/features/master/MasterDashboardPage.jsx";
import { ClientsPage } from "@/features/master/ClientsPage.jsx";
import { OnboardingPage } from "@/features/master/OnboardingPage.jsx";
import { SubscriptionsPage } from "@/features/master/SubscriptionsPage.jsx";
import { InvoicesPage } from "@/features/master/InvoicesPage.jsx";
import { UsagePage } from "@/features/master/UsagePage.jsx";
import { SupportPage } from "@/features/master/SupportPage.jsx";
import { MasterUsersPage } from "@/features/master/MasterUsersPage.jsx";
import { AuditPage } from "@/features/master/AuditPage.jsx";
import { MasterSettingsPage } from "@/features/master/MasterSettingsPage.jsx";
import { NotFoundPage } from "@/shared/pages/NotFoundPage.jsx";
import { ErrorPage } from "@/shared/pages/ErrorPage.jsx";
import { PLACEHOLDER_MODULES, makePlaceholderPage } from "@/shared/pages/ComingSoon.jsx";
import { PLATFORM_ROLES, CLINIC_ROLES } from "@/shared/lib/roles.js";

/**
 * Mapa de rotas do app.
 *
 * Todas as URLs ficam declaradas aqui. O app antigo usava roteamento por
 * arquivo com um `routeTree.gen.ts` gerado de 887 linhas — para saber quais
 * telas existiam era preciso listar a pasta e deduzir as regras de nome.
 */

/** Quem pode abrir o sistema operacional: a clínica, e o Master em visita. */
const ACESSO_CLINICA = [...CLINIC_ROLES, ...PLATFORM_ROLES];

export const router = createBrowserRouter([
  {
    errorElement: <ErrorPage />,
    children: [
      // ------------------------------------------------------------ público
      { path: "/", element: <LandingPage /> },
      { path: "/privacidade", element: <LegalPage documento="privacidade" /> },
      { path: "/termos", element: <LegalPage documento="termos" /> },
      {
        path: "/login",
        element: (
          <RedirectIfAuthenticated>
            <LoginPage />
          </RedirectIfAuthenticated>
        ),
      },

      // -------------------------------------------------- área autenticada
      {
        element: <RequireAuth />,
        children: [
          // ------------------------------------------ sistema da clínica
          {
            element: <RequireRole roles={ACESSO_CLINICA} />,
            children: [
              { path: "/dashboard", element: <DashboardPage /> },
              { path: "/leads", element: <LeadsPage /> },
              { path: "/crm", element: <CrmPage /> },
              { path: "/agenda", element: <AgendaPage /> },
              { path: "/whatsapp", element: <WhatsAppPage /> },
              { path: "/automacao", element: <AutomationPage /> },
              { path: "/integracoes", element: <IntegrationsPage /> },
              { path: "/integracoes/whatsapp-meta", element: <WhatsAppMetaPage /> },
              { path: "/configuracoes", element: <SettingsPage /> },
              { path: "/usuarios", element: <UsersPage /> },

              // Atalho antigo mantido: /whatsapp-meta levava à configuração.
              {
                path: "/whatsapp-meta",
                element: <Navigate to="/integracoes/whatsapp-meta" replace />,
              },

              // Os 11 módulos reservados, gerados a partir de uma lista.
              ...PLACEHOLDER_MODULES.map((modulo) => {
                const Pagina = makePlaceholderPage(modulo);
                return { path: modulo.path, element: <Pagina /> };
              }),
            ],
          },

          // ------------------------------------------------ painel Master
          {
            element: <RequireRole roles={PLATFORM_ROLES} />,
            children: [
              { path: "/master", element: <MasterDashboardPage /> },
              { path: "/master/clientes", element: <ClientsPage /> },
              { path: "/master/onboarding", element: <OnboardingPage /> },
              { path: "/master/assinaturas", element: <SubscriptionsPage /> },
              { path: "/master/cobrancas", element: <InvoicesPage /> },
              { path: "/master/utilizacao", element: <UsagePage /> },
              { path: "/master/suporte", element: <SupportPage /> },
              { path: "/master/usuarios", element: <MasterUsersPage /> },
              { path: "/master/auditoria", element: <AuditPage /> },
              { path: "/master/configuracoes", element: <MasterSettingsPage /> },
            ],
          },
        ],
      },

      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
